import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  MediaListState,
  MediaDetailsState,
  MediaChaptersState,
  Host,
  AssetsState,
  LiveListState,
  PresetListState,
  SubtitleListState,
  HomeListState,
  PresetListModelState,
  AdminPresetListState,
  AdminCurrentPresetDetailsState,
  BucketListState,
  SectionEntityState,
  GlobalFavSectionListState,
  PriorityListState,
  PrioritySectionEntry,
  PrioritySectionsListState,
  ArticleInfoListState,
  AddedInListState,
  DenmarkTaxonomyCodeFetchState,
  ArticleSearchListState,
  DenmarkEntityListState,
  LivePreviewState,
  LiveUrlState,
  AiVoicesState,
  AiLanguagesState,
  AiTextToAudioState,
  AiTextToDialogueState,
  ChunkInfoState,
} from "./media.models";
import {
  MediaModel,
  HostSubSection,
  ChapterInterface,
  ChunkInfoItem,
  CreditModel,
  LiveMeta,
  SubtitleModel,
  PresetListModel,
  SocialEmbedsAgg,
  AdminPreset,
  AllAdminPresets,
  BucketModel,
  SectionEntity,
  PriorityListResponse,
  PriorityListAPIResponse,
  ArticleInfoList,
  AddedIn,
  Taxonomy,
  DenmarkTaxonomyCodeFetchItem,
  Keyword,
  ArticleSearchItem,
  LivePreviewItem,
  LiveUrlItem,
  CategoryTagsResult,
} from "@/database";
import { VoicesResponse, LanguagesResponse } from "@/database/constants/model";
import { AssetItem } from "@/database";
import { MEDIA_STATUS } from "@/utils/constants/uiConstants";

// The backend only includes content_type:2 (drive) / ctype:"premiere" on the
// first media.update event for an item - later events for the same id omit
// it. Remember ids seen with these markers so later events still route correctly.
const driveMediaIds = new Set<string>();
const premiereMediaIds = new Set<string>();

const initialState = {
  mediaList: {
    list: [] as Array<MediaModel>,
    count: 0,
    isLoading: false,
    error: "",
  },
  presetMediaList: {
    list: [] as Array<PresetListModel>,
    count: 0,
    isLoading: false,
    error: "",
  },
  preMediaList: {
    list: [] as Array<MediaModel>,
    count: 0,
    isLoading: false,
    error: "",
  },
  postMediaList: {
    list: [] as Array<MediaModel>,
    count: 0,
    isLoading: false,
    error: "",
  },
  parentMediaList: {
    list: [] as Array<MediaModel>,
    count: 0,
    isLoading: false,
    error: "",
  },
  // Populated by getListMedia/getSearchedListMedia callers outside the main
  // MediaList page (media pickers in meta presets, social publish slates,
  // social settings, etc.) via key: "secondary", so they don't clobber
  // mediaList — which the MediaList page relies on staying untouched when
  // navigated away from and back.
  secondaryMediaList: {
    list: [] as Array<MediaModel>,
    count: 0,
    isLoading: false,
    error: "",
  },
  hostList: {
    list: [] as Array<Host>,
    isLoading: false,
    error: "",
  },
  hostSubSection: {
    list: [] as Array<HostSubSection>,
    isLoading: false,
    error: "",
  },
  pendingSocialUpdatesByMediaId: {} as Record<string, any[]>,
  // Media ids currently undergoing a "Replace" reupload - tracked in redux
  // (not component state) so the in-progress indicator survives navigating
  // away from and back to the media detail page.
  replacingMediaIds: {} as Record<string, boolean>,
  // Socials borrowed from another media via "Reuse Meta" -> select media,
  // to be pre-opened on the Social Publish page for the media being edited.
  borrowedMediaSocials: {
    sourceMediaId: "" as string,
    socials: [] as Array<any>,
  },
  // Playlists borrowed from another media via "Reuse Meta" -> select media,
  // to be merged into the Playlist section for the media being edited.
  borrowedMediaPlaylists: {
    sourceMediaId: "" as string,
    playlists: [] as Array<any>,
    playlistsData: [] as Array<any>,
  },
  mediaDetails: {
    isLoading: false,
    isUpdating: false,
    media: {} as MediaModel,
    isError: "",
  },
  previewMediaDetails: {
    isLoading: false,
    isError: "",
    media: {} as MediaModel,
  },
  mediaAssets: {
    isLoading: false,
    assets: [] as Array<AssetItem>,
    error: "",
  },
  homeList: {
    isLoading: false,
    error: "",
    media_today_agg: {} as any,
    media_yesterday_agg: {} as any,
    youtube_data_agg: {} as any,
    video_analytics_agg: {} as any,
    social_embeds_agg: {} as SocialEmbedsAgg,
  },
  mediaChapters: {
    mediaId: "",
    list: [] as Array<ChapterInterface>,
    count: 0,
    isLoading: true,
    error: "",
    isSavingChapter: false,
    isDeletingChapter: false,
    currentOpenedChapter: {} as ChapterInterface,
  },
  mediaPublish: {
    isPublishingMedia: false,
  },
  mediaDetailsSuccessfullySaved: false,
  creditList: {
    isLoading: false,
    error: "",
    list: [] as Array<CreditModel>,
  },
  audioCreditList: {
    isLoading: false,
    error: "",
    list: [] as Array<CreditModel>,
  },
  videoCreditList: {
    isLoading: false,
    error: "",
    list: [] as Array<CreditModel>,
  },
  imageCreditList: {
    isLoading: false,
    error: "",
    list: [] as Array<CreditModel>,
  },
  activeCustomUploadTab: "video",

  isFilter: false,
  isClearFilter: false,
  isThumbnailUploading: false,
  isMandatoryMetaFilled: true,
  currentImage: null as string | null,
  currentImageId: null as string | null,
  liveList: {
    list: [] as Array<LiveMeta>,
    isLoading: false,
    error: "",
  },
  categoryList: {
    list: [] as Array<any>,
    isLoading: false,
    error: "",
  },
  subtitleList: {
    list: [] as Array<SubtitleModel>,
    isLoading: false,
    error: "",
  },
  chunkInfo: {
    uploadId: "",
    list: [] as Array<ChunkInfoItem>,
    isLoading: false,
    error: "",
  },
  presetList: {
    list: [] as Array<PresetListModel>,
    isLoading: true,
    error: "",
  },
  adminPresetList: {
    list: {} as AllAdminPresets,
    isLoading: true,
    error: "",
  },
  adminBucketList: {
    list: [] as Array<BucketModel>,
    isLoading: true,
    error: "",
  },
  adminCurrentPresetDetails: {
    isLoading: false,
    error: "",
    data: {} as AdminPreset,
  },
  currentPresetDetails: {
    isLoading: false,
    error: "",
    data: {} as PresetListModel,
  },
  denmarkTaxonomyList: {
    list: [] as Array<Taxonomy>,
    isLoading: false,
    error: "",
  },
  denmarkTaxonomyCodeFetch: {
    list: [] as Array<DenmarkTaxonomyCodeFetchItem>,
    isLoading: false,
    error: "",
  },
  denmarkSectionList: {
    list: [] as Array<SectionEntity>,
    isLoading: false,
    error: "",
  },
  denmarkEntityList: {
    list: [] as Array<SectionEntity>,
    isLoading: false,
    error: "",
  },
  priorityList: {
    list: {} as PriorityListAPIResponse,
    isLoading: false,
    error: "",
  },
  prioritySectionsList: {
    list: [] as PrioritySectionEntry[],
    activeSectionId: null as number | null,
    isFetchingSection: false,
    globalEntry: null as PrioritySectionEntry | null,
  } as PrioritySectionsListState,
  articleInfoList: {
    list: {} as ArticleInfoList,
    isLoading: false,
    error: "",
  },
  articleSearchList: {
    list: [] as ArticleSearchItem[],
    isLoading: false,
    error: "",
  },
  livePreview: {
    data: null as LivePreviewItem | null,
    isLoading: false,
    error: "",
  },
  liveUrl: {
    data: null as LiveUrlItem | null,
    isLoading: false,
    error: "",
  },
  FavoriteDenmarkSectionList: {
    list: [] as Array<SectionEntity>,
    isLoading: false,
    error: "",
  },
  GlobalFavSectionList: {
    list: [] as Array<SectionEntity>,
    isLoading: false,
    error: "",
  },

  AddedInList: {
    list: {} as AddedIn,
    isLoading: false,
    error: "",
  },
  isEditPageChanged: false,
  previousFilters: {} as any,
  keywordList: {
    list: [] as Array<Keyword>,
    isLoading: false,
    error: null,
  },
  categoryTagsResult: {
    data: null as CategoryTagsResult | null,
    isLoading: false,
    error: null as string | null,
  },
  // tempThumbnailImage: null as string | null,
  // tempThumbnailImageId: null as string | null,
  aiVoices: {
    voices: {} as VoicesResponse,
    isLoading: false,
    error: "",
  },
  aiLanguages: {
    languages: {} as LanguagesResponse,
    isLoading: false,
    error: "",
  },
  aiTextToAudio: {
    result: null as any,
    isLoading: false,
    error: "",
  },
  aiTextToDialogue: {
    result: null as any,
    isLoading: false,
    error: "",
  },
};

export const MediaSlice = createSlice({
  name: "mediaSlice",
  initialState,
  reducers: {
    setMediaList: (state, { payload }: PayloadAction<MediaListState>) => {
      state.mediaList = {
        ...state.mediaList,
        isLoading: payload?.isLoading ?? state.mediaList.isLoading,
        error: payload?.error ?? state.mediaList.error,
        list: payload?.data?.list ?? state.mediaList.list,
        count: payload?.data?.count ?? state.mediaList.count,
      };
    },
    setCurrentMediaDetails: (
      state,
      { payload }: PayloadAction<MediaDetailsState>
    ) => {
      state.mediaDetails = {
        ...state.mediaDetails,
        media: { ...state.mediaDetails.media, ...(payload?.data?.media ?? {}) },
      };
    },
    setPresetMediaList: (
      state,
      { payload }: PayloadAction<PresetListState>
    ) => {
      state.presetMediaList = {
        ...state.presetMediaList,
        isLoading: payload?.isLoading ?? state.presetMediaList.isLoading,
        error: payload?.error ?? state.presetMediaList.error,
        list: payload?.data?.list ?? state.presetMediaList.list,
        count: payload?.data?.count ?? state.presetMediaList.count,
      };
    },
    setMediaReplacing: (
      state,
      { payload }: PayloadAction<{ id: string; isReplacing: boolean }>
    ) => {
      if (payload.isReplacing) {
        state.replacingMediaIds[payload.id] = true;
      } else {
        delete state.replacingMediaIds[payload.id];
      }
    },
    updateMediaStatus: (state, action) => {
      const { id, status } = action.payload;
      const item = state.mediaList.list.find((m) => m.id === id);
      if (item) {
        item.status = status;
        item.state = status;
      }
    },
    setHomeList: (state, { payload }: PayloadAction<HomeListState>) => {
      state.homeList = {
        ...state.homeList,
        isLoading: payload?.isLoading ?? state.homeList.isLoading,
        error: payload?.error ?? state.homeList.error,
        media_today_agg:
          payload?.media_today_agg ?? state.homeList.media_today_agg,
        social_embeds_agg:
          payload?.social_embeds_agg ?? state.homeList.social_embeds_agg,
        media_yesterday_agg:
          payload?.media_yesterday_agg ?? state.homeList.media_yesterday_agg,
        youtube_data_agg:
          payload?.youtube_data_agg ?? state.homeList.youtube_data_agg,
        video_analytics_agg:
          payload?.video_analytics_agg ?? state.homeList.video_analytics_agg,
      };
    },
    setLiveList: (state, { payload }: PayloadAction<LiveListState>) => {
      state.liveList = {
        ...state.liveList,
        isLoading: payload?.isLoading ?? state.liveList.isLoading,
        error: payload?.error ?? state.liveList.error,
        list: payload?.data?.list ?? state.liveList.list,
        // count: payload?.data?.count ?? state.liveList.count,
      };
    },
    setPresetList: (state, { payload }: PayloadAction<PresetListState>) => {
      state.presetList = {
        ...state.presetList,
        isLoading: payload?.isLoading ?? state.presetList.isLoading,
        error: payload?.error ?? state.presetList.error,
        list: payload?.data?.list ?? state.presetList.list,
      };
    },
    setChunkInfo: (state, { payload }: PayloadAction<ChunkInfoState>) => {
      state.chunkInfo = {
        ...state.chunkInfo,
        isLoading: payload?.isLoading ?? state.chunkInfo.isLoading,
        error: payload?.error ?? state.chunkInfo.error,
        uploadId: payload?.data?.uploadId ?? state.chunkInfo.uploadId,
        list: payload?.data?.list ?? state.chunkInfo.list,
      };
    },
    setDenmarkSectionList: (
      state,
      { payload }: PayloadAction<SectionEntityState>
    ) => {
      state.denmarkSectionList = {
        ...state.denmarkSectionList,
        isLoading: payload?.isLoading ?? state.denmarkSectionList.isLoading,
        error: payload?.error ?? state.denmarkSectionList.error,
        list: payload?.data?.list ?? state.denmarkSectionList.list,
      };
    },
    setDenmarkEntityList: (
      state,
      { payload }: PayloadAction<DenmarkEntityListState>
    ) => {
      state.denmarkEntityList = {
        ...state.denmarkEntityList,
        isLoading: payload?.isLoading ?? state.denmarkEntityList.isLoading,
        error: payload?.error ?? state.denmarkEntityList.error,
        list: payload?.data?.list ?? state.denmarkEntityList.list,
      };
    },
    setPriorityList: (state, { payload }: PayloadAction<PriorityListState>) => {
      state.priorityList = {
        ...state.priorityList,
        isLoading: payload?.isLoading ?? state.priorityList.isLoading,
        error: payload?.error ?? state.priorityList.error,
        list: payload?.data?.list ?? state.priorityList.list,
      };
    },

    /** Add a section or refresh it if already present. */
    upsertSectionInPriorityList: (
      state,
      { payload }: PayloadAction<PrioritySectionEntry>
    ) => {
      const idx = state.prioritySectionsList.list.findIndex(
        (e) => e.sectionId === payload.sectionId
      );
      const merged = { ...payload, deletedList: [] };
      if (idx !== -1) {
        state.prioritySectionsList.list[idx] = merged;
      } else {
        state.prioritySectionsList.list.push(merged);
      }
      state.prioritySectionsList.activeSectionId = payload.sectionId;
    },

    setActivePrioritySectionId: (
      state,
      { payload }: PayloadAction<number | null>
    ) => {
      state.prioritySectionsList.activeSectionId = payload;
    },

    /** Update the ordered priorityList items for a section (after drag-and-drop). */
    updateSectionItemsInPriorityList: (
      state,
      {
        payload,
      }: PayloadAction<{
        sectionId: number;
        reorderedItems: any[];
        newlyAddedList?: any[];
        deletedList?: any[];
      }>
    ) => {
      const entry = state.prioritySectionsList.list.find(
        (e) => e.sectionId === payload.sectionId
      );
      if (!entry) return;
      entry.priorityList = {
        ...entry.priorityList,
        priorityList: {
          ...entry.priorityList?.priorityList,
          priorityListEntity: {
            ...entry.priorityList?.priorityList?.priorityListEntity,
            priorityList: payload.reorderedItems,
          },
        },
      };
      if (payload.newlyAddedList !== undefined) {
        entry.newlyAddedList = payload.newlyAddedList;
      }
      if (payload.deletedList !== undefined) {
        entry.deletedList = payload.deletedList;
      }
    },

    removeSectionFromPriorityList: (
      state,
      { payload: sectionId }: PayloadAction<number>
    ) => {
      state.prioritySectionsList.list = state.prioritySectionsList.list.filter(
        (e) => e.sectionId !== sectionId
      );
      if (state.prioritySectionsList.activeSectionId === sectionId) {
        const remaining = state.prioritySectionsList.list;
        state.prioritySectionsList.activeSectionId =
          remaining.length > 0
            ? remaining[remaining.length - 1].sectionId
            : null;
      }
    },

    /** Sync the server-returned priorityListEntity back after a successful save. */
    syncSavedSectionPriorityList: (
      state,
      { payload }: PayloadAction<{ sectionId: number; priorityListEntity: any }>
    ) => {
      const entry = state.prioritySectionsList.list.find(
        (e) => e.sectionId === payload.sectionId
      );
      if (!entry) return;
      entry.newlyAddedList = [];
      entry.deletedList = [];
      entry.priorityList = {
        ...entry.priorityList,
        priorityList: {
          ...entry.priorityList?.priorityList,
          priorityListEntity: payload.priorityListEntity,
        },
      };
    },

    setPriorityFetching: (state, { payload }: PayloadAction<boolean>) => {
      state.prioritySectionsList.isFetchingSection = payload;
    },

    setGlobalPriorityEntry: (
      state,
      { payload }: PayloadAction<PrioritySectionEntry | null>
    ) => {
      state.prioritySectionsList.globalEntry = payload;
    },

    /** Reset everything when the priority panel is closed. */
    clearPrioritySectionsList: (state) => {
      state.prioritySectionsList = {
        list: [],
        activeSectionId: null,
        isFetchingSection: false,
        globalEntry: null,
      };
    },

    clearDenmarkSectionList: (state) => {
      state.denmarkSectionList = {
        list: [],
        isLoading: true,
        error: "",
      };
    },
    setArticleInfoList: (
      state,
      { payload }: PayloadAction<ArticleInfoListState>
    ) => {
      state.articleInfoList = {
        ...state.articleInfoList,
        isLoading: payload?.isLoading ?? state.articleInfoList.isLoading,
        error: payload?.error ?? state.articleInfoList.error,
        list: payload?.data?.list ?? state.articleInfoList.list,
      };
    },
    setArticleSearchList: (
      state,
      { payload }: PayloadAction<ArticleSearchListState>
    ) => {
      state.articleSearchList = {
        ...state.articleSearchList,
        isLoading: payload?.isLoading ?? state.articleSearchList.isLoading,
        error: payload?.error ?? state.articleSearchList.error,
        list: payload?.data?.list ?? state.articleSearchList.list,
      };
    },
    setLivePreview: (state, { payload }: PayloadAction<LivePreviewState>) => {
      state.livePreview = {
        ...state.livePreview,
        isLoading: payload?.isLoading ?? state.livePreview.isLoading,
        error: payload?.error ?? state.livePreview.error,
        data: payload?.data?.data ?? state.livePreview.data,
      };
    },
    setLiveUrl: (state, { payload }: PayloadAction<LiveUrlState>) => {
      state.liveUrl = {
        ...state.liveUrl,
        isLoading: payload?.isLoading ?? state.liveUrl.isLoading,
        error: payload?.error ?? state.liveUrl.error,
        data: payload?.data?.data ?? state.liveUrl.data,
      };
    },
    setFavoriteDenmarkSectionList: (
      state,
      { payload }: PayloadAction<SectionEntityState>
    ) => {
      state.FavoriteDenmarkSectionList = {
        ...state.FavoriteDenmarkSectionList,
        isLoading:
          payload?.isLoading ?? state.FavoriteDenmarkSectionList.isLoading,
        error: payload?.error ?? state.FavoriteDenmarkSectionList.error,
        list: payload?.data?.list ?? state.FavoriteDenmarkSectionList.list,
      };
    },
    setGlobalFavSectionList: (
      state,
      { payload }: PayloadAction<GlobalFavSectionListState>
    ) => {
      state.GlobalFavSectionList = {
        ...state.GlobalFavSectionList,
        isLoading: payload?.isLoading ?? state.GlobalFavSectionList.isLoading,
        error: payload?.error ?? state.GlobalFavSectionList.error,
        list: payload?.data?.list ?? state.GlobalFavSectionList.list,
      };
    },
    setAddedInList: (state, { payload }: PayloadAction<AddedInListState>) => {
      state.AddedInList = {
        ...state.AddedInList,
        isLoading: payload?.isLoading ?? state.AddedInList.isLoading,
        error: payload?.error ?? state.AddedInList.error,
        list: payload?.data?.list ?? state.AddedInList.list,
      };
    },
    setAdminPresetList: (
      state,
      { payload }: PayloadAction<AdminPresetListState>
    ) => {
      state.adminPresetList = {
        ...state.adminPresetList,
        isLoading: payload?.isLoading ?? state.adminPresetList.isLoading,
        error: payload?.error ?? state.adminPresetList.error,
        list: payload?.data?.list ?? state.adminPresetList.list,
      };
    },
    setCurrentPresetDetails: (
      state,
      { payload }: PayloadAction<PresetListModelState>
    ) => {
      state.currentPresetDetails = {
        ...state.currentPresetDetails,
        isLoading: payload?.isLoading ?? state.currentPresetDetails.isLoading,
        error: payload?.error ?? state.currentPresetDetails.error,
        data: payload?.data?.list ?? state.currentPresetDetails.data,
      };
    },
    setAdminCurrentPresetDetails: (
      state,
      { payload }: PayloadAction<AdminCurrentPresetDetailsState>
    ) => {
      state.adminCurrentPresetDetails = {
        ...state.adminCurrentPresetDetails,
        isLoading:
          payload?.isLoading ?? state.adminCurrentPresetDetails.isLoading,
        error: payload?.error ?? state.adminCurrentPresetDetails.error,
        data: payload?.data?.list ?? state.adminCurrentPresetDetails.data,
      };
    },
    setPreMediaList: (state, { payload }: PayloadAction<MediaListState>) => {
      state.preMediaList = {
        ...state.preMediaList,
        isLoading: payload?.isLoading ?? state.preMediaList.isLoading,
        error: payload?.error ?? state.preMediaList.error,
        list: payload?.data?.list ?? state.preMediaList.list,
        count: payload?.data?.count ?? state.preMediaList.count,
      };
    },
    setAdminBucketList: (
      state,
      { payload }: PayloadAction<BucketListState>
    ) => {
      state.adminBucketList = {
        ...state.adminBucketList,
        isLoading: payload?.isLoading ?? state.adminBucketList.isLoading,
        error: payload?.error ?? state.adminBucketList.error,
        list: payload?.data?.list ?? state.adminBucketList.list,
      };
    },

    setDenmarkTaxonomyList: (state, { payload }: PayloadAction<any>) => {
      state.denmarkTaxonomyList = {
        ...state.denmarkTaxonomyList,
        isLoading: payload?.isLoading ?? state.denmarkTaxonomyList.isLoading,
        error: payload?.error ?? state.denmarkTaxonomyList.error,
        list: payload?.data?.list ?? state.denmarkTaxonomyList.list,
      };
    },
    setDenmarkTaxonomyCodeFetch: (
      state,
      { payload }: PayloadAction<DenmarkTaxonomyCodeFetchState>
    ) => {
      state.denmarkTaxonomyCodeFetch = {
        ...state.denmarkTaxonomyCodeFetch,
        isLoading:
          payload?.isLoading ?? state.denmarkTaxonomyCodeFetch.isLoading,
        error: payload?.error ?? state.denmarkTaxonomyCodeFetch.error,
        list: payload?.data?.list ?? state.denmarkTaxonomyCodeFetch.list,
      };
    },
    setPostMediaList: (state, { payload }: PayloadAction<MediaListState>) => {
      state.postMediaList = {
        ...state.postMediaList,
        isLoading: payload?.isLoading ?? state.postMediaList.isLoading,
        error: payload?.error ?? state.postMediaList.error,
        list: payload?.data?.list ?? state.postMediaList.list,
        count: payload?.data?.count ?? state.postMediaList.count,
      };
    },
    setCategoryList: (state, { payload }: PayloadAction<any>) => {
      state.categoryList = {
        ...state.categoryList,
        list: payload?.data?.list ?? state.categoryList.list,
        isLoading: payload?.isLoading ?? state.categoryList.isLoading,
        error: payload?.error ?? state.categoryList.error,
      };
    },
    setParentMediaList: (state, { payload }: PayloadAction<MediaListState>) => {
      state.parentMediaList = {
        ...state.parentMediaList,
        isLoading: payload?.isLoading ?? state.parentMediaList.isLoading,
        error: payload?.error ?? state.parentMediaList.error,
        list: payload?.data?.list ?? state.parentMediaList.list,
        count: payload?.data?.count ?? state.parentMediaList.count,
      };
    },
    setSecondaryMediaList: (
      state,
      { payload }: PayloadAction<MediaListState>
    ) => {
      state.secondaryMediaList = {
        ...state.secondaryMediaList,
        isLoading: payload?.isLoading ?? state.secondaryMediaList.isLoading,
        error: payload?.error ?? state.secondaryMediaList.error,
        list: payload?.data?.list ?? state.secondaryMediaList.list,
        count: payload?.data?.count ?? state.secondaryMediaList.count,
      };
    },

    setSubtitleList: (state, { payload }: PayloadAction<SubtitleListState>) => {
      state.subtitleList = {
        ...state.subtitleList,
        isLoading: payload?.isLoading ?? state.subtitleList.isLoading,
        error: payload?.error ?? state.subtitleList.error,
        list: payload?.data?.list ?? state.subtitleList.list,
        // count: payload?.data?.count ?? state.subtitleList.count,
      };
    },

    setIsEditPageChanged: (state, { payload }: PayloadAction<boolean>) => {
      state.isEditPageChanged = payload;
    },

    updateMediaInMediaList: (
      state,
      { payload }: PayloadAction<{ media: any }>
    ) => {
      if (!payload?.media) return;
      const mediaId = String(payload.media.id);
      if (payload.media.content_type === 2) {
        driveMediaIds.add(mediaId);
      }
      if (payload.media.ctype === "premiere") {
        premiereMediaIds.add(mediaId);
      }
      const isDriveItem =
        payload.media.content_type === 2 || driveMediaIds.has(mediaId);
      const isPremiereItem =
        payload.media.ctype === "premiere" || premiereMediaIds.has(mediaId);

      const mediaIndex = state.mediaList.list.findIndex(
        (m) => m.id === payload.media.id
      );
      if (mediaIndex !== -1) {
        // Update existing media
        state.mediaList.list[mediaIndex] = {
          ...state.mediaList.list[mediaIndex],
          ...payload.media,
        };
      } else if (
        payload.media.media_status !== MEDIA_STATUS.MEDIA_STATUS_INACTIVE
      ) {
        // Add new media only if status is 1 (active) - never (re)add a
        // deleted media_status (3) item that isn't already in the list.
        const currentPath = window.location.pathname;
        const mediaPathMatch = currentPath.match(/^\/media\/([^/]+)/);
        const pathAssetType = mediaPathMatch ? mediaPathMatch[1] : null;
        const isOnPremiereTab = pathAssetType?.toLowerCase() === "premiere";

        let shouldAppend: boolean;
        if (isDriveItem) {
          // Drive items only ever surface on their own drive routes -
          // video drive uploads on /drive/video, shorts drive uploads on /media/shorts.
          if (payload.media.asset_type === "video") {
            shouldAppend = currentPath.startsWith("/drive/video");
          } else if (payload.media.asset_type === "shorts") {
            shouldAppend = currentPath.startsWith("/media/shorts");
          } else {
            shouldAppend = false;
          }
        } else {
          shouldAppend = isPremiereItem
            ? isOnPremiereTab
            : Boolean(
                pathAssetType &&
                  payload.media.asset_type &&
                  pathAssetType.toLowerCase() ===
                    payload.media.asset_type.toLowerCase()
              );
        }

        if (shouldAppend) {
          state.mediaList.list.unshift(payload.media);
          state.mediaList.count += 1;
        }
      }
    },

    updateSocialInMediaList: (
      state,
      { payload }: PayloadAction<{ media: any }>
    ) => {
      // console.warn('media.social.slice:', payload);
      const mediaIndex = state.mediaList?.list?.findIndex(
        (m) => m.id === payload.media.media
      );
      // console.warn('media.social.slice:', mediaIndex);
      if (mediaIndex !== -1) {
        // console.warn('media.social.slice: index:found', mediaIndex);
        const updatedMediaItem = {
          ...state.mediaList.list[mediaIndex],
          socials: state.mediaList.list[mediaIndex].socials
            ? [...state.mediaList.list[mediaIndex].socials]
            : [],
        };
        // console.warn('media.social.slice:: ', updatedMediaItem);
        const socialIndex = updatedMediaItem.socials.findIndex(
          (s: any) => s.id === payload.media.id
        );
        // console.warn('media.social.slice::socialIndex:', socialIndex);
        if (socialIndex !== -1) {
          // console.warn('media.social.slice:socialIndex: found', socialIndex);
          updatedMediaItem.socials[socialIndex] = {
            ...updatedMediaItem.socials[socialIndex],
            ...payload.media,
          };
          // console.warn('media.social.slice:', updatedMediaItem);
        } else {
          // console.warn('media.social.slice:socialIndex: not found', socialIndex);
          updatedMediaItem.socials.push(payload.media);
          // console.warn('media.social.slice: :being pushed ', payload.media);
        }
        state.mediaList.list[mediaIndex] = updatedMediaItem;
        // console.warn('media.social.slice: final updatedMediaItem', updatedMediaItem);
      }
    },

    updateSocialInMediaDetails: (
      state,
      { payload }: PayloadAction<{ social: any }>
    ) => {
      if (!payload?.social?.id) return;

      const currentSocials = state.mediaDetails.media.socials || [];
      const socialIndex = currentSocials.findIndex(
        (s: any) => s.id === payload.social.id
      );

      if (socialIndex !== -1) {
        // Update existing social
        state.mediaDetails.media.socials[socialIndex] = {
          ...state.mediaDetails.media.socials[socialIndex],
          ...payload.social,
        };
      } else {
        // Add new social if not found
        state.mediaDetails.media.socials = [...currentSocials, payload.social];
      }
    },
    upsertSocialInMediaDetails: (
      state,
      { payload }: PayloadAction<{ social: any }>
    ) => {
      if (!payload?.social?.id) return;

      const incoming = payload.social;
      const currentSocials: any[] = Array.isArray(
        state.mediaDetails.media.socials
      )
        ? [...state.mediaDetails.media.socials]
        : [];

      const existingIndex = currentSocials.findIndex(
        (item: any) => String(item.id) === String(incoming.id)
      );

      if (existingIndex !== -1) {
        currentSocials[existingIndex] = {
          ...currentSocials[existingIndex],
          ...incoming,
        };
      } else {
        currentSocials.push(incoming);
      }

      const uniqueSocials = currentSocials.reduce(
        (acc: any[], current: any) => {
          const isDuplicate = acc.find(
            (item) =>
              String(item.id) === String(current.id) ||
              (item.channel &&
                current.channel &&
                String(item.channel) === String(current.channel) &&
                item.type === current.type &&
                item.social === current.social)
          );
          if (!isDuplicate) {
            acc.push(current);
          }
          return acc;
        },
        []
      );

      state.mediaDetails.media.socials = uniqueSocials;
    },
    removePendingSocialUpdate: (
      state,
      { payload }: PayloadAction<{ mediaId: string; socialId: string | number }>
    ) => {
      if (!payload?.mediaId || payload?.socialId == null) return;
      const mediaIdStr = payload.mediaId;
      const list = state.pendingSocialUpdatesByMediaId[mediaIdStr];
      if (!list) return;
      state.pendingSocialUpdatesByMediaId[mediaIdStr] = list.filter(
        (item: any) => String(item.id) !== String(payload.socialId)
      );
    },
    addPendingSocialUpdate: (
      state,
      { payload }: PayloadAction<{ mediaId: string; social: any }>
    ) => {
      if (!payload?.mediaId || !payload?.social?.id) return;
      const mediaIdStr = payload.mediaId;
      const { social } = payload;
      const list = state.pendingSocialUpdatesByMediaId[mediaIdStr] ?? [];
      const existingIndex = list.findIndex(
        (item: any) => String(item.id) === String(social.id)
      );
      const next = [...list];
      if (existingIndex !== -1) {
        next[existingIndex] = { ...next[existingIndex], ...social };
      } else {
        next.push(social);
      }
      const unique = next.reduce((acc: any[], current: any) => {
        const isDuplicate = acc.find(
          (item) =>
            String(item.id) === String(current.id) ||
            (item.channel &&
              current.channel &&
              String(item.channel) === String(current.channel) &&
              item.type === current.type &&
              item.social === current.social)
        );
        if (!isDuplicate) acc.push(current);
        return acc;
      }, []);
      state.pendingSocialUpdatesByMediaId[mediaIdStr] = unique;
    },
    setMediaDetails: (state, { payload }: PayloadAction<MediaDetailsState>) => {
      const incomingMedia = payload?.data?.media ?? {};
      const mergedMedia = {
        ...state.mediaDetails.media,
        ...incomingMedia,
      };
      const mediaId = mergedMedia?.id;
      const mediaIdStr = mediaId != null ? String(mediaId) : "";
      const pending = mediaIdStr
        ? state.pendingSocialUpdatesByMediaId[mediaIdStr]
        : undefined;

      const hasIncomingSocials =
        incomingMedia &&
        Object.prototype.hasOwnProperty.call(incomingMedia, "socials");

      const shouldConsumePending =
        payload?.data?.consumePendingSocials === true || hasIncomingSocials;

      if (
        shouldConsumePending &&
        mediaIdStr &&
        Array.isArray(pending) &&
        pending.length > 0
      ) {
        const baseSocials = Array.isArray(mergedMedia.socials)
          ? [...mergedMedia.socials]
          : [];
        const combined = [...baseSocials];
        for (const social of pending) {
          const idx = combined.findIndex(
            (s: any) => String(s.id) === String(social.id)
          );
          if (idx !== -1) {
            combined[idx] = { ...combined[idx], ...social };
          } else {
            combined.push(social);
          }
        }
        const uniqueSocials = combined.reduce((acc: any[], current: any) => {
          const isDuplicate = acc.find(
            (item) =>
              String(item.id) === String(current.id) ||
              (item.channel &&
                current.channel &&
                String(item.channel) === String(current.channel) &&
                item.type === current.type &&
                item.social === current.social)
          );
          if (!isDuplicate) acc.push(current);
          return acc;
        }, []);
        mergedMedia.socials = uniqueSocials;
        delete state.pendingSocialUpdatesByMediaId[mediaIdStr];
      }
      state.mediaDetails = {
        ...state.mediaDetails,
        isLoading: payload?.isLoading ?? state.mediaDetails.isLoading,
        isError: payload?.error ?? state.mediaDetails.isError,
        media: mergedMedia,
        isUpdating: payload?.data?.isUpdating ?? state.mediaDetails.isUpdating,
      };
    },
    setPreviewMediaDetails: (
      state,
      { payload }: PayloadAction<MediaDetailsState>
    ) => {
      state.previewMediaDetails = {
        ...state.previewMediaDetails,
        isLoading: payload?.isLoading ?? state.previewMediaDetails.isLoading,
        isError: payload?.error ?? state.previewMediaDetails.isError,
        media: {
          ...state.previewMediaDetails.media,
          ...(payload?.data?.media ?? {}),
        },
      };
    },
    setMediaAssets: (state, { payload }: PayloadAction<AssetsState>) => {
      state.mediaAssets = {
        isLoading: payload?.isLoading ?? state.mediaAssets.isLoading,
        error: payload?.error ?? state.mediaAssets.error,
        assets: payload?.data?.assets ?? state.mediaAssets.assets,
      };
    },

    addMediaAsset: (
      state,
      { payload }: PayloadAction<{ asset: AssetItem }>
    ) => {
      state.mediaAssets.assets.push(payload.asset);
    },
    setPreviousFilters: (state, { payload }: PayloadAction<any>) => {
      state.previousFilters = payload;
    },
    addMediaInMediaList: (
      state,
      { payload }: PayloadAction<{ media: MediaModel }>
    ) => {
      const { media } = payload;
      state.mediaList.list.unshift(media);
      state.mediaList.count += 1;
    },
    setHostList: (state, { payload }: PayloadAction<any>) => {
      state.hostList = {
        ...state.hostList,
        isLoading: payload?.isLoading ?? state.hostList.isLoading,
        error: payload?.error ?? state.hostList.error,
        list: payload?.list ?? state.hostList.list,
      };
    },
    setHostSubsection: (state, { payload }: PayloadAction<any>) => {
      state.hostSubSection = {
        ...state.hostSubSection,
        isLoading: payload?.isLoading ?? state.hostSubSection.isLoading,
        error: payload?.error ?? state.hostSubSection.error,
        list: payload?.list ?? state.hostSubSection.list,
      };
    },
    setIsUpdatingMediaDetails: (state, action) => {
      state.mediaDetails.isUpdating = action.payload;
    },
    emptyMediaDetails: (state) => {
      state.mediaDetails.media = {} as MediaModel;
    },

    emptyAssetDetails: (state) => {
      state.mediaAssets.assets = [];
    },
    resetMediaDetails: (state) => {
      state.mediaDetails = initialState.mediaDetails;
      state.pendingSocialUpdatesByMediaId = {};
      state.borrowedMediaSocials = initialState.borrowedMediaSocials;
      state.borrowedMediaPlaylists = initialState.borrowedMediaPlaylists;
    },
    setMediaChapters: (
      state,
      { payload }: PayloadAction<MediaChaptersState>
    ) => {
      state.mediaChapters = {
        ...state.mediaChapters,
        mediaId: payload?.data?.mediaId ?? state.mediaChapters.mediaId,
        list: payload?.data?.list ?? state.mediaChapters.list,
        count: payload?.data?.count ?? state.mediaChapters.count,
        isLoading: payload?.isLoading ?? state.mediaChapters.isLoading,
        error: payload?.error ?? state.mediaChapters.error,
        isSavingChapter:
          payload?.data?.isSavingChapter ?? state.mediaChapters.isSavingChapter,
        isDeletingChapter:
          payload?.data?.isDeletingChapter ??
          state.mediaChapters.isDeletingChapter,
      };
    },
    setCurrentOpenedChapter: (
      state,
      { payload }: PayloadAction<ChapterInterface>
    ) => {
      state.mediaChapters.currentOpenedChapter = payload;
    },
    resetMediaChapters: (state) => {
      state.mediaChapters = initialState.mediaChapters;
    },
    setisFilter: (state, action) => {
      state.isFilter = action.payload;
    },
    setIsClearFilter: (state, action) => {
      state.isClearFilter = action.payload;
    },
    setActiveCustomUploadTab: (state, { payload }: PayloadAction<string>) => {
      state.activeCustomUploadTab = payload;
    },
    setCurrentImage: (state, { payload }: PayloadAction<string | null>) => {
      state.currentImage = payload;
    },
    setCurrentImageId: (state, { payload }: PayloadAction<string | null>) => {
      state.currentImageId = payload;
    },
    // setTempThumbnailImage: (state, { payload }: PayloadAction<string | null>) => {
    //   state.tempThumbnailImage = payload;
    // },
    // setTempThumbnailImageId: (state, { payload }: PayloadAction<string | null>) => {
    //   state.tempThumbnailImageId = payload;
    // },
    setIsThumbnailUploading: (state, { payload }: PayloadAction<boolean>) => {
      state.isThumbnailUploading = payload;
    },
    setIsDeletingChapter: (state, action) => {
      state.mediaChapters.isDeletingChapter = action.payload;
    },
    setIsSavingChapter: (state, action) => {
      state.mediaChapters.isSavingChapter = action.payload;
    },

    // **** chapters ****
    addChapter: (state, action) => {
      const chapter = action.payload;
      state.mediaChapters.list = state.mediaChapters.list.filter(
        (c) => c.id !== "new_chapter"
      );
      state.mediaChapters.list = [...state.mediaChapters.list, chapter];
    },

    updateChapter: (state, action) => {
      const updatedChapters = state.mediaChapters.list.map((chapter) => {
        if (chapter.id === action.payload.result.id) {
          return {
            ...action.payload.result,
            clip_id: action.payload.result.clip_id || chapter.clip_id,
            clip_start: action.payload.result.clip_start || chapter.clip_start,
            clip_end: action.payload.result.clip_end || chapter.clip_end,
          };
        }
        return chapter;
      });
      state.mediaChapters.list = [...updatedChapters];

      if (
        state.mediaChapters.currentOpenedChapter.id === action.payload.result.id
      ) {
        state.mediaChapters.currentOpenedChapter = {
          ...action.payload.result,
          clip_id:
            action.payload.result.clip_id ||
            state.mediaChapters.currentOpenedChapter.clip_id,
          clip_start:
            action.payload.result.clip_start ||
            state.mediaChapters.currentOpenedChapter.clip_start,
          clip_end:
            action.payload.result.clip_end ||
            state.mediaChapters.currentOpenedChapter.clip_end,
        };
      }
    },

    deleteChapter: (state, action) => {
      const chapterId = action.payload;
      state.mediaChapters.list = state.mediaChapters.list.filter(
        (chapter) => chapter.id !== chapterId
      );
    },

    setIsLoadingMediaChapters: (state, action) => {
      state.mediaChapters.isLoading = action.payload;
    },
    setChapterList: (state, action) => {
      state.mediaChapters.isLoading = true;
      state.mediaChapters.count = action.payload.count;
      state.mediaChapters.list = [...action.payload.list];
      state.mediaChapters.isLoading = false;
    },

    setIsPublishingMedia: (state, { payload }: PayloadAction<boolean>) => {
      state.mediaPublish.isPublishingMedia = payload;
    },

    setMediaAsPublished: (state, _action) => {
      state.mediaPublish = initialState.mediaPublish;
    },

    resetMediaPublish: (state, _action) => {
      state.mediaPublish = initialState.mediaPublish;
    },
    setCreditList: (state, { payload }) => {
      state.creditList = {
        ...state.creditList,
        list: payload?.data?.list ?? state.creditList.list,
        isLoading: payload?.isLoading ?? state.creditList.isLoading,
        error: payload?.error ?? state.creditList.error,
      };
    },
    setAudioCreditList: (state, { payload }) => {
      state.audioCreditList = {
        ...state.audioCreditList,
        list: payload?.data?.list ?? state.audioCreditList.list,
        isLoading: payload?.isLoading ?? state.audioCreditList.isLoading,
        error: payload?.error ?? state.audioCreditList.error,
      };
    },
    setVideoCreditList: (state, { payload }) => {
      state.videoCreditList = {
        ...state.videoCreditList,
        list: payload?.data?.list ?? state.videoCreditList.list,
        isLoading: payload?.isLoading ?? state.videoCreditList.isLoading,
        error: payload?.error ?? state.videoCreditList.error,
      };
    },
    setImageCreditList: (state, { payload }) => {
      state.imageCreditList = {
        ...state.imageCreditList,
        list: payload?.data?.list ?? state.imageCreditList.list,
        isLoading: payload?.isLoading ?? state.imageCreditList.isLoading,
        error: payload?.error ?? state.imageCreditList.error,
      };
    },
    setIsMandatoryMetaFilled: (state, { payload }: PayloadAction<boolean>) => {
      state.isMandatoryMetaFilled = payload;
    },
    setKeywordList: (state, { payload }) => {
      state.keywordList = {
        ...state.keywordList,
        list: payload?.data?.list ?? state.keywordList.list,
        isLoading: payload?.isLoading ?? state.keywordList.isLoading,
        error: payload?.error ?? state.keywordList.error,
      };
    },
    setCategoryTagsResult: (state, { payload }) => {
      state.categoryTagsResult = {
        ...state.categoryTagsResult,
        data: payload?.data ?? state.categoryTagsResult.data,
        isLoading: payload?.isLoading ?? state.categoryTagsResult.isLoading,
        error: payload?.error ?? state.categoryTagsResult.error,
      };
    },
    setAiVoices: (state, { payload }: PayloadAction<AiVoicesState>) => {
      state.aiVoices = {
        ...state.aiVoices,
        voices: payload?.data?.voices ?? state.aiVoices.voices,
        isLoading: payload?.isLoading ?? state.aiVoices.isLoading,
        error: payload?.error ?? state.aiVoices.error,
      };
    },
    setAiLanguages: (state, { payload }: PayloadAction<AiLanguagesState>) => {
      state.aiLanguages = {
        ...state.aiLanguages,
        languages: payload?.data?.languages ?? state.aiLanguages.languages,
        isLoading: payload?.isLoading ?? state.aiLanguages.isLoading,
        error: payload?.error ?? state.aiLanguages.error,
      };
    },
    emptyAiVoices: (state) => {
      state.aiVoices = {
        voices: {} as VoicesResponse,
        isLoading: false,
        error: "",
      };
    },
    emptyAiLanguages: (state) => {
      state.aiLanguages = {
        languages: {} as LanguagesResponse,
        isLoading: false,
        error: "",
      };
    },
    setAiTextToAudio: (
      state,
      { payload }: PayloadAction<AiTextToAudioState>
    ) => {
      state.aiTextToAudio = {
        ...state.aiTextToAudio,
        result: payload?.data?.result ?? state.aiTextToAudio.result,
        isLoading: payload?.isLoading ?? state.aiTextToAudio.isLoading,
        error: payload?.error ?? state.aiTextToAudio.error,
      };
    },
    setAiTextToDialogue: (
      state,
      { payload }: PayloadAction<AiTextToDialogueState>
    ) => {
      state.aiTextToDialogue = {
        ...state.aiTextToDialogue,
        result: payload?.data?.result ?? state.aiTextToDialogue.result,
        isLoading: payload?.isLoading ?? state.aiTextToDialogue.isLoading,
        error: payload?.error ?? state.aiTextToDialogue.error,
      };
    },
    setBorrowedMediaSocials: (
      state,
      { payload }: PayloadAction<{ sourceMediaId: string; socials: any[] }>
    ) => {
      state.borrowedMediaSocials = {
        sourceMediaId: payload?.sourceMediaId || "",
        socials: payload?.socials || [],
      };
    },
    clearBorrowedMediaSocials: (state) => {
      state.borrowedMediaSocials = initialState.borrowedMediaSocials;
    },
    setBorrowedMediaPlaylists: (
      state,
      {
        payload,
      }: PayloadAction<{
        sourceMediaId: string;
        playlists: any[];
        playlistsData: any[];
      }>
    ) => {
      state.borrowedMediaPlaylists = {
        sourceMediaId: payload?.sourceMediaId || "",
        playlists: payload?.playlists || [],
        playlistsData: payload?.playlistsData || [],
      };
    },
    clearBorrowedMediaPlaylists: (state) => {
      state.borrowedMediaPlaylists = initialState.borrowedMediaPlaylists;
    },
  },
});

// creating actions from reducers
export const {
  setMediaList,
  setMediaDetails,
  setMediaAssets,
  addMediaAsset,
  emptyAssetDetails,
  setHostList,
  addMediaInMediaList,
  setHostSubsection,
  setIsUpdatingMediaDetails,
  addChapter,
  deleteChapter,
  setIsLoadingMediaChapters,
  setChapterList,
  emptyMediaDetails,
  setMediaChapters,
  resetMediaChapters,
  resetMediaDetails,
  setIsDeletingChapter,
  setIsSavingChapter,
  updateChapter,
  setIsPublishingMedia,
  updateMediaInMediaList,
  setCreditList,
  setPostMediaList,
  setPreMediaList,
  updateMediaStatus,
  setMediaReplacing,
  setisFilter,
  setIsThumbnailUploading,
  setIsMandatoryMetaFilled,
  setCurrentImage,
  updateSocialInMediaList,
  updateSocialInMediaDetails,
  upsertSocialInMediaDetails,
  addPendingSocialUpdate,
  removePendingSocialUpdate,
  setCurrentImageId,
  setLiveList,
  setParentMediaList,
  setSecondaryMediaList,
  setIsClearFilter,
  setAudioCreditList,
  setVideoCreditList,
  setImageCreditList,
  setActiveCustomUploadTab,
  setPresetList,
  setChunkInfo,
  setCurrentOpenedChapter,
  setSubtitleList,
  setCategoryList,
  setPresetMediaList,
  setHomeList,
  setPreviousFilters,
  setPreviewMediaDetails,
  setCurrentPresetDetails,
  setAdminPresetList,
  setAdminCurrentPresetDetails,
  setAdminBucketList,
  setIsEditPageChanged,
  setDenmarkTaxonomyList,
  setDenmarkTaxonomyCodeFetch,
  setDenmarkSectionList,
  setDenmarkEntityList,
  setPriorityList,
  upsertSectionInPriorityList,
  setActivePrioritySectionId,
  updateSectionItemsInPriorityList,
  removeSectionFromPriorityList,
  syncSavedSectionPriorityList,
  clearPrioritySectionsList,
  clearDenmarkSectionList,
  setPriorityFetching,
  setGlobalPriorityEntry,
  setArticleInfoList,
  setArticleSearchList,
  setLivePreview,
  setLiveUrl,
  setFavoriteDenmarkSectionList,
  setGlobalFavSectionList,
  setAddedInList,
  setKeywordList,
  setCategoryTagsResult,
  setAiVoices,
  setAiLanguages,
  emptyAiVoices,
  emptyAiLanguages,
  setAiTextToAudio,
  setAiTextToDialogue,
  setBorrowedMediaSocials,
  clearBorrowedMediaSocials,
  setBorrowedMediaPlaylists,
  clearBorrowedMediaPlaylists,
} = MediaSlice.actions;

export default MediaSlice.reducer;
